// HomeAbout.js — the short explainer under the home grid: what the site is,
// how to read the grid, and where to go next. It gives the home page real
// text and headings for search engines (the grid itself is mostly numbers)
// without pushing the grid down: it sits below the first screen.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { BRAND } from "../../theme";

const MORE = [
  { to: "/scorelines", label: "Every Premier League scoreline", note: "most common to rarest" },
  { to: "/records", label: "Premier League records", note: "biggest wins, highest scores" },
  { to: "/all-time-table", label: "All-time Premier League table", note: "any season or span" },
  { to: "/seasons", label: "Every Premier League season", note: "champions, tables and records" },
  { to: "/head-to-head", label: "Head to head records", note: "every fixture since 1992" },
  { to: "/watch", label: "Scorigami Watch", note: "this round's odds of a new result" },
  { to: "/race", label: "Title race chart", note: "every season, match by match" },
  { to: "/clubs", label: "Every club's record", note: "all clubs since 1992" },
  { to: "/articles/what-is-scorigami", label: "What is a scorigami?", note: "the idea explained" },
];

const h2Sx = { color: "#fff", fontSize: { xs: 19, sm: 20 }, fontWeight: 800, letterSpacing: "-0.01em", mb: 1.25 };
const pSx = { color: "#c9ccd6", fontSize: 15, lineHeight: 1.75, mb: 1.5 };
const linkSx = { color: BRAND.cyan, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } };

export default function HomeAbout() {
  return (
    <Box
      component="section"
      aria-label="About the grid"
      sx={{
        maxWidth: 1180,
        mx: "auto",
        mt: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
        pt: { xs: 4, md: 5 },
        borderTop: "1px solid rgba(255,255,255,0.08)",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
        gap: { xs: 3.5, md: 5 },
      }}
    >
      <Box>
        <Typography component="h2" sx={h2Sx}>
          What is Premier League Scorigami?
        </Typography>
        <Typography sx={pSx}>
          A scorigami is a final score that has never happened before. This grid holds every Premier League result since
          the league began in August 1992, more than 13,000 matches. Each square is a final score, coloured by how often it
          has happened; the empty squares are scores the league has never produced.
        </Typography>
        <Typography sx={pSx}>
          When a match ends in a brand-new score, the grid gains a square.{" "}
          <Box component="a" href="https://x.com/pl_gami" target="_blank" rel="noopener noreferrer" sx={linkSx}>
            @pl_gami
          </Box>{" "}
          posts every result as it happens and flags the new ones.
        </Typography>
      </Box>

      <Box>
        <Typography component="h2" sx={h2Sx}>
          How to read the grid
        </Typography>
        <Typography sx={pSx}>
          In the Home / Away view, one axis counts the home side's goals and the other the away side's, so a 3–1 home win
          and a 1–3 away win are different squares. Switch to Winner / Loser to count them as one result.
        </Typography>
        <Typography sx={pSx}>
          Pick any square to see when that score first and last happened and how often it has come up each season, with a
          link to every match that finished that way. Choose a single season to see how one campaign filled the grid.
        </Typography>
      </Box>

      <Box component="nav" aria-labelledby="home-more">
        <Typography component="h2" id="home-more" sx={h2Sx}>
          More from the archive
        </Typography>
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.25 }}>
          {MORE.map((m) => (
            <Box component="li" key={m.to} sx={{ fontSize: 15, lineHeight: 1.5 }}>
              <Box component={RouterLink} to={m.to} sx={linkSx}>
                {m.label}
              </Box>
              <Box component="span" sx={{ color: BRAND.textSecondary }}>
                {" "}
                · {m.note}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
